import React, {PureComponent} from "react";
import {DialogManagerProps} from "../../../../../store/dialog-manager/types";
import {Overlay} from "../../../overlay/Overlay";
import renderText from "../../../renderText";
import styles from "./form-dialog.module.css";
import dialogStyles from "../dialog.module.css";
import formStyles from "../../../../styles/forms.module.css";
import {cssHelper} from "../../../../utils/cssHelper";
import playSound from "../../../../sounds/playSound";
import {iif} from "../../../iif";
import i18n from "../../../../i18n";

interface FormField {
    type?: 'text' | 'email' | 'textarea';
    name: string;
    value?: string;
    label?: string;
    attributes?: Record<string, any>;
}

interface FormDialogProps {
    title: string;
    fields: FormField[];
    onClose?: (value: Record<string, string>) => void
}

interface FormDialogState {
    isFocused: boolean;
    form: Record<string, string>
}

export default class FormDialog extends PureComponent<FormDialogProps & DialogManagerProps, FormDialogState> {
    protected form: React.RefObject<HTMLFormElement> = React.createRef();
    private isMobile: boolean;

    constructor(props: FormDialogProps & DialogManagerProps, context: any) {
        super(props, context);

        this.controlChanged = this.controlChanged.bind(this);
        this.focus = this.focus.bind(this);
        this.blur = this.blur.bind(this);

        this.isMobile = navigator.userAgent.includes("Chrome") && 'ontouchstart' in window;

        this.state = {
            isFocused: false,
            form: props.fields.reduce((fields, field) => ({
                ...fields,
                [field.name]: field.value
            }), {})
        };
    }

    render() {
        const fields = this.renderFields();

        return (
            <Overlay>
                <div className={cssHelper(dialogStyles.container, styles.form_container).add(styles.form_container_focus, this.isMobile && this.state.isFocused).toString()}>
                    {iif(!!this.props.title, renderText(i18n(this.props.title as any), 36, 56, 14, 25, styles.text))}
                    <form className={formStyles.form_group} ref={this.form} onSubmit={(event) => event.preventDefault()}>
                        {fields}
                    </form>
                    <div className={dialogStyles.actions}>
                        <button className={cssHelper(dialogStyles.button, dialogStyles.secondary_button).toString()} onClick={() => this.back()}>
                            {i18n("questionsubmit.cancel")}
                        </button>
                        <button className={dialogStyles.button} onClick={() => this.submit()}>
                            {i18n("questionsubmit.submit")}
                        </button>
                    </div>
                </div>
            </Overlay>
        );
    }

    componentDidMount(): void {
        if (!this.form.current)
            return;

        const field = this.form.current.querySelector(`input, textarea`) as HTMLInputElement;
        if (!field)
            return;

        setTimeout(() => {
            field.focus();
            field.selectionStart = field.selectionEnd = field.value.length;
        }, 300);
    }

    private renderFields() {
        return this.props.fields.map((field, i) => (
            <div key={i} className={formStyles.control_group}>
                {this.renderField(field)}
            </div>
        ));
    }

    private renderField(field: FormField) {
        switch (field.type) {
            case "textarea":
                return (
                    <textarea name={field.name} rows={1} className={formStyles.control} value={this.state.form[field.name]} {...field.attributes} onFocus={this.focus} onBlur={this.blur} onChange={(e) => this.textareaChanged(e)}/>
                );

            default:
                return (
                    <input name={field.name} type={field.type || "text"} className={formStyles.control} value={this.state.form[field.name]} {...field.attributes} onChange={this.controlChanged}/>
                );
        }
    }

    private controlChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        this.setState({
            form: {
                ...this.state.form,
                [event.target.name]: event.target.value
            }
        });
    }

    private submit() {
        playSound("button-click");

        if (this.form.current && this.form.current.checkValidity && !this.form.current.checkValidity())
            return;

        this.props.close({
            value: this.state.form
        });

        if (this.props.onClose) {
            this.props.onClose(this.state.form);
        }
    }

    private back() {
        playSound("button-click");
        this.props.close();
    }

    private textareaChanged(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.controlChanged(event);

        if (event.target) {
            event.target.style.height = "5px";
            event.target.style.height = (event.target.scrollHeight) + "px";
        }
    }

    private focus() {
        this.setState({
            isFocused: true
        });
    }

    private blur() {
        this.setState({
            isFocused: false
        });
    }
};